// src/components/dashboard/SideBySideViewer.jsx
import React, { useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Flex,
    Box,
    Image,
    Text,
    Heading,
    Divider,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    useColorMode,
    useBreakpointValue,
    SimpleGrid
} from '@chakra-ui/react';
import { FiDownload, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { downloadWithWhiteBackground } from '../../utils/imageUtils';

// Componente para visualizar duas imagens lado a lado
const SideBySideViewer = ({ isOpen, onClose, originalImage, processedImage, title, onDownload, onDelete }) => {
    const [sliderValue, setSliderValue] = useState(50);
    const { colorMode } = useColorMode();
    
    // Definir tamanhos responsivos
    const buttonSize = useBreakpointValue({ base: "sm", md: "md" });
    const columns = useBreakpointValue({ base: 1, sm: 2, md: 4 });
    
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            motionPreset="slideInBottom"
            isCentered
        >
            <ModalOverlay />
            <ModalContent maxW={{ base: "90%", md: "800px" }}>
                <ModalHeader>
                    <Text noOfLines={1}>{title || "Comparação de Imagens"}</Text>
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody pb={4}>
                    <Flex direction="column" width="100%">
                        {/* Título e controles */}
                        <Heading size="sm" color="gray.600" mb={4}>
                            Arraste o controle para comparar
                        </Heading>

                        {/* Container das imagens com slider */}
                        <Box
                            position="relative"
                            width="100%"
                            height="400px"
                            mb={4}
                            borderRadius="md"
                            overflow="hidden"
                            border="1px solid"
                            borderColor={colorMode === "light" ? "gray.200" : "gray.600"}
                        >
                            {/* Imagem original (fundo) */}
                            <Image
                                src={originalImage}
                                alt="Imagem original"
                                objectFit="contain"
                                width="100%"
                                height="100%"
                            />

                            {/* Imagem processada (frente com clip) */}
                            <Box
                                position="absolute"
                                top="0"
                                left="0"
                                width={`${sliderValue}%`}
                                height="100%"
                                overflow="hidden"
                                borderRight="2px solid"
                                borderColor="blue.500"
                            >
                                <Image
                                    src={processedImage}
                                    alt="Imagem processada"
                                    objectFit="contain"
                                    width={`${10000 / sliderValue}%`}
                                    height="100%"
                                    style={{ transform: `translateX(-${100 - sliderValue}%)` }}
                                />

                                {/* Indicador de imagem */}
                                <Text
                                    position="absolute"
                                    top="10px"
                                    left="10px"
                                    bg="rgba(0, 0, 0, 0.7)"
                                    color="white"
                                    px={2}
                                    py={1}
                                    borderRadius="md"
                                    fontSize="sm"
                                >
                                    Processada
                                </Text>
                            </Box>

                            {/* Indicador de imagem original */}
                            <Text
                                position="absolute"
                                top="10px"
                                right="10px"
                                bg="rgba(0, 0, 0, 0.7)"
                                color="white"
                                px={2}
                                py={1}
                                borderRadius="md"
                                fontSize="sm"
                            >
                                Original
                            </Text>

                            {/* Controle deslizante vertical */}
                            <Box
                                position="absolute"
                                left={`${sliderValue}%`}
                                top="0"
                                height="100%"
                                width="2px"
                                bg="blue.500"
                                cursor="ew-resize"
                                zIndex="10"
                                transform="translateX(-50%)"
                                _hover={{ bg: "blue.600" }}
                                _active={{ bg: "blue.700" }}
                            >
                                {/* Círculo do controle deslizante */}
                                <Box
                                    position="absolute"
                                    top="50%"
                                    left="50%"
                                    transform="translate(-50%, -50%)"
                                    bg="white"
                                    border="2px solid"
                                    borderColor="blue.500"
                                    borderRadius="full"
                                    width="20px"
                                    height="20px"
                                    cursor="ew-resize"
                                    boxShadow="md"
                                />
                            </Box>
                        </Box>

                        {/* Slider horizontal para controlar a divisão */}
                        <Slider
                            aria-label="Slider para comparar imagens"
                            value={sliderValue}
                            onChange={setSliderValue}
                            min={0}
                            max={100}
                            step={1}
                            mb={6}
                            colorScheme="blue"
                        >
                            <SliderTrack>
                                <SliderFilledTrack />
                            </SliderTrack>
                            <SliderThumb boxSize={6} />
                        </Slider>

                        <Divider mb={4} />

                        {/* Botões de ação em grid responsivo */}
                        <SimpleGrid columns={columns} spacing={3} mb={2}>
                            <Button
                                leftIcon={<FiDownload />}
                                colorScheme="blue"
                                variant="outline"
                                onClick={() => downloadWithWhiteBackground(originalImage, `${title || 'imagem'}_original.jpg`)}
                                size={buttonSize}
                                width="100%"
                            >
                                Baixar Original
                            </Button>
                            
                            <Button
                                leftIcon={<FiDownload />}
                                colorScheme="purple"
                                variant="outline"
                                onClick={() => downloadWithWhiteBackground(processedImage, `${title || 'imagem'}_processada.jpg`)}
                                size={buttonSize}
                                width="100%"
                            >
                                Baixar Processada
                            </Button>
                            
                            <Button
                                leftIcon={<FiRefreshCw />}
                                onClick={() => setSliderValue(50)}
                                variant="ghost"
                                size={buttonSize}
                                width="100%"
                            >
                                Resetar
                            </Button>

                            {/* Botão de exclusão */}
                            {onDelete && (
                                <Button
                                    leftIcon={<FiTrash2 />}
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={() => {
                                        onClose();
                                        onDelete();
                                    }}
                                    size={buttonSize}
                                    width="100%"
                                >
                                    Excluir
                                </Button>
                            )}
                        </SimpleGrid>
                    </Flex>
                </ModalBody>

                <ModalFooter>
                    <Button 
                        onClick={onClose}
                        width="100%"
                    >
                        Fechar
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SideBySideViewer;